import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { type Me } from "@/lib/backend/api";
import { logger } from "@/logger";
import { queryOptions_me } from "../providers/query/functions/user/me";
import { useSessionProvider } from "../providers/session/use-session";

const SESSION_TIMEOUT_MS = 60000; // 60s for mobile network tolerance and app resume scenarios

export function useAuthType() {
  const { session } = useSessionProvider();
  const [sessionTimedOut, setSessionTimedOut] = useState(false);

  // Timeout mechanism: If session stays pending for too long, force it to resolve as no session
  useEffect(() => {
    // Reset timeout flag when session resolves (either successfully or with error)
    // This handles the edge case where session resolves after timeout
    if (!session.isPending) {
      if (sessionTimedOut) {
        logger.info("[Auth] Session resolved after timeout, resetting timeout flag", {
          hasSessionData: !!session.data,
        });
      }
      setSessionTimedOut(false);
      return;
    }

    logger.info("[Auth] Session check started");

    // Warn at 75% of timeout duration
    const warningTimer = setTimeout(() => {
      if (session.isPending) {
        logger.warn(
          {
            elapsed: SESSION_TIMEOUT_MS * 0.75,
            remaining: SESSION_TIMEOUT_MS * 0.25,
          },
          "[Auth] Session check taking longer than expected",
        );
      }
    }, SESSION_TIMEOUT_MS * 0.75);

    const timer = setTimeout(() => {
      if (session.isPending) {
        logger.warn(
          {
            timeout: SESSION_TIMEOUT_MS,
            sessionState: session.isPending,
          },
          "[Auth] Session check timed out - treating as unauthenticated",
        );
        setSessionTimedOut(true);
      }
    }, SESSION_TIMEOUT_MS);

    return () => {
      clearTimeout(warningTimer);
      clearTimeout(timer);
    };
  }, [session.isPending]);

  const {
    data: me,
    isLoading: isLoadingMe,
    isError: isErrorMe,
    refetch: refetchMe,
  } = useQuery({
    ...queryOptions_me({}),
    enabled: !!session.data,
  });

  // `me?.adminUser !== null` is `true` while `me` is undefined, because `undefined !== null`. That
  // window is not rare — the query is disabled whenever `session.data` is momentarily gone — and
  // during it every employee reads as an admin, which sends the routers redirecting them to the
  // admin home and back. Nobody is an admin until `me` actually says so.
  const isAdmin = !!me && me.adminUser !== null && !isLoadingMe;

  // Only show loading on initial load, not on refetches
  // This prevents white screen when invalidating queries (e.g., profile picture upload)
  // If there's an error, don't show loading to prevent infinite loading state
  // If session timed out, don't show loading (treat as no session)
  const isLoading =
    (session.isPending && !sessionTimedOut) || (!!session.data && isLoadingMe && !me && !isErrorMe);

  // If session timed out, treat as unauthenticated
  const isSignedIn = sessionTimedOut ? false : !!session.data;
  const isEmployee = sessionTimedOut ? false : !!session.data && (me as Me)?.employee !== null;

  // Log authentication state changes for debugging
  useEffect(() => {
    if (!isLoading) {
      logger.info(
        {
          isSignedIn,
          isEmployee,
          isAdmin,
          hasMe: !!me,
          sessionTimedOut,
        },
        "[Auth] Authentication state resolved",
      );
    }
  }, [isLoading, isSignedIn, isEmployee, isAdmin, me, sessionTimedOut]);

  return {
    isLoading,
    isSignedIn,
    isEmployee,
    isAdmin,
    me,
    refetchMe,
    session,
    isLoadingMe,
  };
}
